/**
 * Lägg till inloggningsmetoder på det INLOGGADE kontot — alltid
 * ägarskapsbevisat innan något kopplas:
 *
 *  - Telefon: Supabase skickar en engångskod via SMS till det nya numret;
 *    koden verifieras med typ 'phone_change'.
 *  - E-post: Supabase skickar en engångskod till den nya adressen;
 *    koden verifieras med typ 'email_change'. Aldrig bekräftelselänk —
 *    PKCE-länkar bryter när mejlet öppnas i en annan webbläsare än PWA:n,
 *    och koden verifieras i den session som ska använda adressen.
 *
 * Kontaktuppgifter som admin fyllt i på en inbjudan blir ALDRIG
 * inloggningsmetoder automatiskt — de kan bara föreslås som förval här,
 * ägarskapet bevisas alltid av koden. profiles_v2 synkas av
 * databastriggern on_auth_user_identity_updated.
 *
 * Driftkrav (Supabase Dashboard): "Secure email change" måste vara AV för
 * att BankID-skapade konton (syntetisk intern adress) ska kunna lägga till
 * e-post — den gamla adressen kan aldrig bekräfta något. Email Change-mallen
 * i Dashboard måste matcha supabase/templates/email_change.html (kod, aldrig
 * gamla/nya adressen i innehållet).
 */

import { supabase } from './supabase';
import { normalizePhoneToE164, isValidPhoneNumber } from './phoneUtils';

/**
 * Starta telefontillägg: skickar engångskod via SMS till numret.
 * Returnerar numret i E.164 (behövs för verifieringssteget).
 */
export async function startPhoneAttach(phone: string): Promise<string> {
  if (!isValidPhoneNumber(phone)) {
    throw new Error('Ogiltigt telefonnummer. Använd formatet 07X-XXX XX XX');
  }
  const phoneE164 = normalizePhoneToE164(phone, 'SE');

  const { error } = await supabase.auth.updateUser({ phone: phoneE164 });
  if (error) {
    throw new Error(mapAttachError(error.message));
  }
  return phoneE164;
}

/** Verifiera engångskoden och aktivera numret som inloggningsmetod. */
export async function verifyPhoneAttach(phoneE164: string, code: string): Promise<void> {
  const { error } = await supabase.auth.verifyOtp({
    phone: phoneE164,
    token: code,
    type: 'phone_change',
  });
  if (error) {
    throw new Error('Fel kod. Kontrollera SMS:et och försök igen.');
  }
}

/**
 * Starta e-posttillägg: skickar en engångskod till adressen.
 * Returnerar den normaliserade adressen (behövs för verifieringssteget).
 */
export async function startEmailAttach(email: string): Promise<string> {
  const trimmed = email.trim();
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
    throw new Error('Ogiltig e-postadress');
  }
  const normalized = trimmed.toLowerCase();

  const { error } = await supabase.auth.updateUser({ email: normalized });
  if (error) {
    throw new Error(mapAttachError(error.message));
  }
  return normalized;
}

/** Verifiera engångskoden och aktivera adressen som inloggningsmetod. */
export async function verifyEmailAttach(email: string, code: string): Promise<void> {
  const { error } = await supabase.auth.verifyOtp({
    email,
    token: code,
    type: 'email_change',
  });
  if (error) {
    throw new Error('Fel kod. Kontrollera mejlet och försök igen.');
  }
}

/** Översätt Supabase-fel till begripliga svenska meddelanden. */
function mapAttachError(message: string): string {
  const lower = message.toLowerCase();
  if (lower.includes('already') && lower.includes('registered')) {
    return 'Uppgiften används redan av ett annat konto.';
  }
  if (lower.includes('rate limit') || lower.includes('too many')) {
    return 'För många försök. Vänta en stund och försök igen.';
  }
  return 'Kunde inte skicka verifieringen. Försök igen.';
}
